import { Flex, Text } from '@mantine/core';
import { useFormContext, useWatch } from 'react-hook-form';
import { InfoBox } from '@dealstack/frontend/shared/components/InfoBox/InfoBox.tsx';
import { RELATED_PEOPLE_FORM_FIELDS } from '@dealstack/frontend/app/pages/Project/Cap/CapEntityManageFlow/tabs/BasicDetails/components/RelatedPeopleDrawer/utils/types.ts';
import {
	useTranslationCapManage,
	useTranslationCommonUi,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { RadioOptionCard } from './RadioOptionCard';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	naturalPersonName: string;
	legalEntityName: string;
	disabled?: boolean;
	fieldName?: string;
}

export const LegalRepresentativeFields = ({
	naturalPersonName,
	legalEntityName,
	disabled,
	fieldName = RELATED_PEOPLE_FORM_FIELDS.create_legal_rep,
}: Props) => {
	const tCommonUi = useTranslationCommonUi();
	const tCapManage = useTranslationCapManage();
	const { control, setValue } = useFormContext();
	const createLegalRep = useWatch({ control, name: fieldName }) !== false;

	const select = (value: boolean) => {
		setValue(fieldName, value, { shouldDirty: true });
	};

	return (
		<InfoBox
			iconVariant="information"
			title={tCapManage('related_people.drawer.legal_rep.title')}
			description={
				<>
					Would you like to make{' '}
					<Text span fw={600} c="dark.8">
						{naturalPersonName}
					</Text>{' '}
					the legal representative for{' '}
					<Text span fw={600} c="dark.8">
						{legalEntityName}
					</Text>
					?
				</>
			}
			fullWidthChildren
		>
			<Flex direction="column" gap="0.5rem">
				<RadioOptionCard
					label={tCommonUi('boolean.true')}
					description={tCapManage('related_people.drawer.legal_rep.yes_description', {
						name: naturalPersonName,
					})}
					checked={createLegalRep}
					disabled={disabled}
					onSelect={() => select(true)}
					dataTestId={TEST_ELEMENT_IDS.CAP_RELATED_PEOPLE_DRAWER_LEGAL_REP_YES}
				/>
				<RadioOptionCard
					label={tCommonUi('boolean.false')}
					description={tCapManage('related_people.drawer.legal_rep.no_description')}
					checked={!createLegalRep}
					disabled={disabled}
					onSelect={() => select(false)}
					dataTestId={TEST_ELEMENT_IDS.CAP_RELATED_PEOPLE_DRAWER_LEGAL_REP_NO}
				/>
			</Flex>
		</InfoBox>
	);
};
